import React, { useMemo, useState } from 'react'
import { Alert, Chip } from '@mui/material'

import { reanalyzeReasoningGap } from '../../service/reasoningGapService'
import { showToast } from '../commonUnit/Toast'

// Submissions tab of a Reasoning Gap session: one row per student, expandable to the code,
// explanation, hidden-test results, per-step gap analysis, the hints the student saw, and
// their hint rating / AI-use survey. Export helpers are shared with the tab bar's Export menu.

const STATUS_COLORS = {
  submitted: 'default', tested: 'info', analyzing: 'info', analyzed: 'success', analysis_failed: 'error',
}

export function download (filename, text, type) {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function csvCell (value) {
  if (value == null) return ''
  const s = typeof value === 'string' ? value : JSON.stringify(value)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// One flat row per submission; per-unit statuses and survey answers become their own columns.
const chatOf = (data, userId) => (data.chats && data.chats[userId]) || []

// Every attempt of every student, oldest first, each carrying the student-level fields
// (email, survey, conversation) of its submission row.
function allAttempts (data) {
  return data.submissions.flatMap(s => [
    ...(s.history || []).map(h => ({ ...h, id: s.id, email: s.email, survey: null, isLatest: false })),
    { ...s, isLatest: true },
  ])
}

// One CSV row per attempt; per-step statuses, ratings and survey answers become columns.
export function toCsv (data) {
  const unitIds = data.rubric.map(u => u.id)
  const attempts = allAttempts(data)
  const surveyKeys = new Set()
  const ratingKeys = new Set()
  attempts.forEach((a) => {
    Object.keys(a.survey || {}).forEach(k => surveyKeys.add(k))
    Object.keys(a.feedback_rating || {}).forEach(k => ratingKeys.add(k))
  })
  const header = [
    'submission_id', 'attempt', 'is_latest_attempt', 'session_id', 'user_id', 'email', 'condition', 'status',
    'problem_id', 'problem_version', 'rubric_version', 'ground_truth_version', 'tests_version',
    'gap_model', 'gap_prompt_version', 'tests_passed', 'tests_total', 'time_on_task_ms',
    'submitted_at', 'analyzed_at', 'code', 'explanation',
    ...unitIds.flatMap(id => [`${id}_code`, `${id}_explanation`]),
    'alignment', 'hints', 'overall_message', 'ai_questions_total', 'ai_conversation',
    ...[...ratingKeys].map(k => `rating_${k}`), ...[...surveyKeys].map(k => `survey_${k}`),
  ]
  const rows = attempts.map((a) => {
    const units = new Map(((a.gap_analysis && a.gap_analysis.units) || []).map(u => [u.unit_id, u]))
    const conversation = a.isLatest ? chatOf(data, a.user_id) : []
    return [
      a.id, a.attempt || 1, a.isLatest, a.session_id, a.user_id, a.email, a.condition, a.status,
      a.problem_id, a.problem_version, a.rubric_version, a.ground_truth_version, a.tests_version,
      a.gap_model, a.gap_prompt_version, a.tests_passed, a.tests_total, a.time_on_task_ms,
      a.submitted_at, a.analyzed_at, a.code, a.explanation,
      ...unitIds.flatMap(id => [units.get(id)?.code_status, units.get(id)?.explanation_status]),
      a.gap_analysis?.code_explanation_alignment,
      (a.student_hints?.hints || []).map(h => `[${h.unit_id}] ${h.hint}`).join('\n'),
      a.student_hints?.overall_message,
      a.isLatest ? conversation.filter(m => m.role === 'user').length : '',
      conversation.map(m => `${m.role === 'user' ? 'Student' : 'Assistant'}: ${m.content}`).join('\n\n'),
      ...[...ratingKeys].map(k => a.feedback_rating?.[k]), ...[...surveyKeys].map(k => a.survey?.[k]),
    ].map(csvCell).join(',')
  })
  return [header.join(','), ...rows].join('\n')
}

// One attempt's work and feedback: code, explanation, hidden tests, per-step analysis, the
// hints the student saw, and their rating of those hints.
function AttemptBody ({ a, data }) {
  const units = (a.gap_analysis && a.gap_analysis.units) || []
  const testsById = new Map(data.tests.map(t => [t.id, t]))
  return (
    <>
      <div className="rg-detail__cols">
        <div>
          <div className="rg-submitted__label">Code</div>
          <pre className="rg-code">{a.code}</pre>
        </div>
        <div>
          <div className="rg-submitted__label">Explanation</div>
          <p className="rg-submitted__text">{a.explanation}</p>
        </div>
      </div>

      {Array.isArray(a.test_results) && (
        <>
          <div className="rg-submitted__label">Hidden tests</div>
          <ul className="rg-detail__tests">
            {a.test_results.map(r => (
              <li key={r.id} className={r.passed ? 'is-pass' : 'is-fail'}>
                {r.passed ? '✓' : '✗'} {testsById.get(r.id)?.description || r.id}
                {r.error && <span className="rg-muted">: {r.error}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {units.length > 0 && (
        <>
          <div className="rg-submitted__label">Gap analysis (alignment: {a.gap_analysis.code_explanation_alignment})</div>
          <table className="rg-units">
            <thead><tr><th>Unit</th><th>Code</th><th>Explanation</th><th>Rationale</th></tr></thead>
            <tbody>
              {units.map(u => (
                <tr key={u.unit_id}>
                  <td>{u.unit_id} {data.rubric.find(r => r.id === u.unit_id)?.title}</td>
                  <td className={`st-${u.code_status}`}>{u.code_status}</td>
                  <td className={`st-${u.explanation_status}`}>{u.explanation_status}</td>
                  <td>{u.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {a.student_hints && (
        <>
          <div className="rg-submitted__label">Shown to student</div>
          <p className="rg-submitted__text">{a.student_hints.overall_message}</p>
          <ol className="rg-feedback__hints">
            {(a.student_hints.hints || []).map((h, i) => <li key={i}><strong>{h.unit_id}</strong> {h.hint}</li>)}
          </ol>
        </>
      )}

      {a.gap_error && <Alert severity="warning" sx={{ my: 1 }}>{a.gap_error}</Alert>}

      {a.feedback_rating && (
        <>
          <div className="rg-submitted__label">Hint rating</div>
          <pre className="rg-json">{JSON.stringify(a.feedback_rating, null, 2)}</pre>
        </>
      )}
    </>
  )
}

function SubmissionDetail ({ s, data, onReanalyze }) {
  const earlier = [...(s.history || [])].reverse()
  return (
    <div className="rg-detail">
      {earlier.length > 0 && <div className="rg-attempt-label">Latest attempt ({s.attempt} of {data.maxAttempts})</div>}
      <AttemptBody a={s} data={data} />
      {earlier.map(h => (
        <details key={h.attempt} className="rg-attempt">
          <summary>Attempt {h.attempt}{h.tests_total != null ? `: ${h.tests_passed}/${h.tests_total} hidden tests` : ''} · {new Date(h.submitted_at).toLocaleString()}</summary>
          <AttemptBody a={h} data={data} />
        </details>
      ))}

      <div className="rg-submitted__label">AI assistant conversation ({chatOf(data, s.user_id).filter(m => m.role === 'user').length} questions)</div>
      {chatOf(data, s.user_id).length ? (
        <div className="rg-transcript">
          {chatOf(data, s.user_id).map(m => (
            <div key={m.id} className={`rg-transcript__msg rg-transcript__msg--${m.role}`}>
              <span className="rg-transcript__who">{m.role === 'user' ? 'Student' : 'Assistant'} · {new Date(m.created_at).toLocaleTimeString()}{m.meta?.blocked ? ' · reply replaced (tried to give away the answer)' : ''}</span>
              <div className="rg-transcript__text">{m.content}</div>
            </div>
          ))}
        </div>
      ) : <p className="rg-muted">Didn't use the assistant.</p>}

      {s.survey && (
        <>
          <div className="rg-submitted__label">AI-use survey</div>
          <pre className="rg-json">{JSON.stringify(s.survey, null, 2)}</pre>
        </>
      )}

      {/* Only for submissions without delivered feedback - see reanalyze in the backend. */}
      {s.status !== 'analyzed' && (
        <div className="rg-actions">
          <button className="gui-exercise-btn" onClick={() => onReanalyze(s.id)}>Re-run gap analysis</button>
        </div>
      )}
    </div>
  )
}

export default function ReasoningGapSubmissions ({ data, error, onChanged }) {
  const [expanded, setExpanded] = useState(null)

  const counts = useMemo(() => {
    const c = {}
    for (const s of data?.submissions || []) c[s.status] = (c[s.status] || 0) + 1
    return c
  }, [data])

  const onReanalyze = async (id) => {
    try {
      await reanalyzeReasoningGap(id)
      showToast('Re-running the gap analysis…', 'info', 2500)
      onChanged()
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error')
    }
  }

  if (error) return <Alert severity="error">{error}</Alert>
  if (!data) return null

  return (
    <div className="rg-submissions">
      <div className="gui-exercise-editor-header">
        <h3>Submissions</h3>
        <div className="rg-submissions__counts">
          {Object.entries(counts).map(([k, v]) => <Chip key={k} size="small" label={`${k}: ${v}`} color={STATUS_COLORS[k]} variant="outlined" />)}
        </div>
      </div>
      <p className="gui-exercise-note">
        Click a row to see the student's latest attempt (code, explanation, hidden tests, how each
        reasoning step was judged for code and explanation, the hints they saw and their rating),
        their earlier attempts, their AI assistant conversation and survey. Export Data CSV has one
        row per attempt. This list refreshes every 15 seconds.
      </p>

      {(() => {
        const submitted = new Set(data.submissions.map(s => String(s.user_id)))
        const pending = Object.keys(data.chats || {}).filter(id => !submitted.has(id)).length
        return pending ? <p className="gui-exercise-note">{pending} student{pending === 1 ? ' is' : 's are'} using the AI assistant but {pending === 1 ? "hasn't" : "haven't"} submitted yet. Their conversations are included in Export Data (JSON).</p> : null
      })()}
      <table className="rg-table">
        <thead>
          <tr><th>Student</th><th>Status</th><th>Attempts</th><th>Tests</th><th>Alignment</th><th>Hints</th><th>AI questions</th><th>Rated</th><th>Used AI</th><th>Submitted</th></tr>
        </thead>
        <tbody>
          {data.submissions.map(s => (
            <React.Fragment key={s.id}>
              <tr className="rg-table__row" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                <td>{[s.first_name, s.last_name].filter(Boolean).join(' ') || s.email || `User ${s.user_id}`}</td>
                <td><Chip size="small" label={s.status} color={STATUS_COLORS[s.status]} /></td>
                <td>{s.attempt}</td>
                <td>{s.tests_total != null ? `${s.tests_passed}/${s.tests_total}` : '-'}</td>
                <td>{s.gap_analysis?.code_explanation_alignment || '-'}</td>
                <td>{s.student_hints ? (s.student_hints.hints || []).length : '-'}</td>
                <td>{chatOf(data, s.user_id).filter(m => m.role === 'user').length}</td>
                <td>{s.feedback_rating ? 'yes' : '-'}</td>
                <td>{s.survey?.used_ai || '-'}</td>
                <td>{new Date(s.submitted_at).toLocaleString()}</td>
              </tr>
              {expanded === s.id && (
                <tr><td colSpan={10}><SubmissionDetail s={s} data={data} onReanalyze={onReanalyze} /></td></tr>
              )}
            </React.Fragment>
          ))}
          {!data.submissions.length && (
            <tr><td colSpan={10} className="rg-muted">No submissions yet. Use Distribute to copy the student link.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
